import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { ReportProvider } from 'src/app/providers';
import { ConfirmPopupComponent, ReportCopyVersionPopupComponent } from '..';
import { saveAs } from 'file-saver';
import { Order } from 'src/app/model';
import { environment } from 'src/environments/environment';
import { LoaderService, ToasterService, UserService } from 'src/app/services';
import { ConnectedToQuickbooksModalComponent, OrderFormConfirmPopupComponent, PreparedForModalComponent, ReleaseThirdPartyModalComponent, ShareReportModalComponent } from 'src/app/pages/dashboard/components';
import {PageNumbers, ReportState, ShareStatus} from '../../model/Enums';
import {TransferReportModalComponent} from '../../pages/dashboard/components/transfer-report-modal/transfer-report-modal.component';

@Component({
  selector: 'app-report-option',
  templateUrl: './report-option.component.html',
  styleUrls: ['./report-option.component.scss']
})
export class ReportOptionComponent implements OnInit {
  @Input() reportId: string;
  @Input() isReportDone: boolean;
  @Input() reportState: ReportState;
  @Input() reportVersionId: string = '';
  @Input() reportName: string = '';
  @Input() isVersionPage: boolean = false;
  @Input() isCurrentVersion: boolean = false;
  @Input() showTop: boolean = false;
  @Input() sharedWithOrg: boolean = false;
  @Input() preparedEntity: string = '';
  @Input() preparedAddress: string = '';
  @Input() createdByUserId: string = '';
  @Input() maxStage: number = null;
  @Input() isShared: boolean = false;
  @Output() reloadPage = new EventEmitter();
  ReportState = ReportState;
  enumPageNumbers = PageNumbers;

  constructor(private reportProvider: ReportProvider,
    private modalService: BsModalService,
    private router: Router,
    public userService: UserService,
    private toastr: ToasterService,
    private loaderService: LoaderService) { }

  ngOnInit(): void {
    // Unused
  }

  /**
   * @description goto report version
   */
  gotoVersionPage() {
    this.router.navigateByUrl(`/dashboard-report?id=${this.reportId}`);
  }

  /**
   * @description create new report version
   */
  copyReportForVersion() {
    let copyRptModalRef: BsModalRef;
    copyRptModalRef = this.modalService.show(ReportCopyVersionPopupComponent, { class: 'modal-lg modal-dialog-centered' });

    const ackSubject = new Subject<any>();
    copyRptModalRef.content.subject = ackSubject;

    ackSubject.subscribe(val => {
      if (val?.res) {

        this.reportProvider.createNewReportVersion(this.reportId, val.name).subscribe(response => {
          if (response.success) {
            this.reloadPage.emit();
            this.toastr.success('Report version added successfully!');
          }
        }, err => {
          // Error Handling
        });
      }
    });
  }

  /**
   * @description for delete report
   */
  deleteReport() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to delete ' + this.reportName + ' report?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {

        this.reportProvider.deleteReport(this.reportId).subscribe(response => {
          if (response.success) {
            this.reloadPage.emit();
            this.toastr.success('Report deleted successfully!');
          }
        }, err => {
          // Error Handling
        });

      }
    });
  }

  /**
   * @description for delete report version
   */
  deleteReportVersion() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to delete this report version?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {

        this.reportProvider.deleteReportVersion(this.reportId, this.reportVersionId).subscribe(response => {
          if (response.success) {
            this.reloadPage.emit();
            this.toastr.success('Report version deleted successfully!');
          }
        }, err => {
          // Error Handling
        });

      }
    });
  }

  /**
   * @description download report PDF
   */
  downloadReportPDF(isOrderThirdParty: boolean = false) {
    const loadingObj = {
      loading: true,
      loadingMessage: 'Preparing Report...'
    }
    this.loaderService.isLoading.next(loadingObj);
    this.toastr.info('Please wait a moment!');
    this.reportProvider.downloadReport(this.reportVersionId).subscribe(response => {
      if (response) {
        if (!isOrderThirdParty) {
          this.saveToFileSystem(response);
        } else {
          if (response.type === 'application/pdf') {
            this.orderThirdPartyReport(response);
          } else {
            this.orderThirdPartyReport(null);
          }
        }
        const loadingObj2 = {
          loading: false,
          loadingMessage: ''
        }
        this.loaderService.isLoading.next(loadingObj2);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description PDF save
   */
  private saveToFileSystem(response) {
    if (this.reportName) {
      const blob = new Blob([response], { type: 'text/pdf' });
      saveAs(blob, this.reportName ? this.reportName + '.pdf' : 'Report.pdf');
    } else {
      this.toastr.error('Report missing important info please complete before downloading!');
    }
  }

  /**
   * @description order third party report
   */
  orderThirdPartyReport(reportPDF: any = null) {

    this.reportProvider.getReportDetailsByVersionId(this.reportId, this.reportVersionId).subscribe(res => {
      if (res.success) {
        const currentReport = res.result;

        let reader = new FileReader();
        if (reportPDF) {
          reader.readAsDataURL(reportPDF);

          reader.onloadend = () => {
            this.openThirdPartyPopup(currentReport, reader);
          };
        } else {
          this.openThirdPartyPopup(currentReport, reader);
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * to open third-party confirm popup
   * */
  openThirdPartyPopup(currentReport: any, reader: FileReader): void {
    let orderConfirmModalRef: BsModalRef;
    orderConfirmModalRef = this.modalService.show(OrderFormConfirmPopupComponent, Object.assign({
      initialState: {
        businessName: currentReport?.LATEST?.COMPANY?.NAME || ''
      }
    }, {class: 'modal-lg modal-dialog-centered'}));
    const ackSubject = new Subject<boolean>();
    orderConfirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        let base64data = reader.result;
        let formData = Order.createFormData(currentReport, this.userService.getCurrentUser(), true);
        this.openUrlInTab(formData, environment.ORDER_FORM_URL, base64data);
      }
    });
  }

  /**
   * @description open third party Url
   */
  private openUrlInTab(obj, url, pdf = null) {
    let mapForm = document.createElement("form");
    mapForm.target = "_blank";
    mapForm.method = "POST";
    mapForm.action = url;

    let mapInput = document.createElement("input");
    mapInput.type = "hidden";
    mapInput.name = "data";
    mapInput.setAttribute("value", JSON.stringify(obj));
    mapForm.appendChild(mapInput);

    if (pdf) {
      let pdfInput = document.createElement("input");
      pdfInput.type = "hidden";
      pdfInput.name = "pdf";
      pdfInput.setAttribute("value", pdf);
      mapForm.appendChild(pdfInput);
    }

    document.body.appendChild(mapForm);
    mapForm.submit();
  }

  /**
   * @description set report version as main report
   */
  setAsMain() {
      this.reportProvider.makeVersionMainReport(this.reportId, this.reportVersionId).subscribe(response => {
        if (response.success) {
          this.reloadPage.emit();
          this.toastr.success('Report versioning changed!');
        }
      }, err => {
        // Error Handling
      });
  }

  /**
   * @description for open share report popup
   */
  shareReportModal() {
    let shareReportModalRef: BsModalRef;
    shareReportModalRef = this.modalService.show(ShareReportModalComponent, Object.assign({
      initialState: {
        companyName: this.reportName,
        reportId: this.reportId,
        reportVersionId: this.reportVersionId,
        sharedWithOrg: this.sharedWithOrg,
        createdBy: this.createdByUserId
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    shareReportModalRef.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        this.reloadPage.emit();
      }
    });
  }

  /**
   * @description for open release third party popup
   */
  openReleaseThirdPartyModal() {
    let releaseReportModalRef: BsModalRef;
    releaseReportModalRef = this.modalService.show(ReleaseThirdPartyModalComponent, Object.assign({
      initialState: {
        businessName: this.reportName,
        reportVersionId: this.reportVersionId
      }
    }, { class: 'modal-lg modal-dialog-centered' }));
  }

  /**
   * @description for open prepared for popup
   */
  openPreparedForModal() {
    let preparedForModalRef: BsModalRef;
    preparedForModalRef = this.modalService.show(PreparedForModalComponent, Object.assign({
      initialState: {
        businessName: this.reportName,
        reportId: this.reportId,
        preparedEntity: this.preparedEntity,
        preparedAddress: this.preparedAddress
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    preparedForModalRef.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        this.reloadPage.emit();
      }
    });
  }

  /**
   * @description for open connected popup
   */
  openConnectedPopup(isConnected: boolean) {
    let connectedToModalRef: BsModalRef;
    connectedToModalRef = this.modalService.show(ConnectedToQuickbooksModalComponent, Object.assign({
      initialState: {
        forConnected: isConnected
      }
    }, { class: 'modal-lg modal-dialog-centered' }));
  }

  /**
   * @description for open transfer report popup
   */
  transferReportModal() {
    let transferReportModalRef: BsModalRef;
    transferReportModalRef = this.modalService.show(TransferReportModalComponent, Object.assign({
      initialState: {
        reportId: this.reportId
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    transferReportModalRef.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        this.reloadPage.emit();
        this.toastr.success('Report transferred successfully!');
      }
    });
  }

}
